import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "../shared/SiteLink";
import { Menu, X, ChevronDown, Phone, Monitor, ShoppingCart, ChevronLeft } from "lucide-react";
import { identity as brandingIdentity } from "../../siteConfig";
import { identity as storeIdentity } from "../../storeSiteConfig";
import { useBusinessInfo } from "../shared/useBusinessInfo";
import { EditableText } from "../shared/EditableText";
import { useContentEditingSafe } from "../shared/ContentEditingContext";
import { useRelativePath, useTemplateType } from "../shared/TemplateContext";
import { useCartSafe } from "../shared/CartContext";
import { useIsCatalogMode } from "../shared/useStoreMode";
import { useCustomNav } from "../shared/useCustomNav";

/** Backend / admin-only page paths — no template nav shown */
const BACKEND_PAGES = [
  "/business-info",
  "/qa-checklist",
  "/site-tree",
  "/seo-manager",
  "/change-history",
  "/activity-log",
  "/media",
];

/**
 * In edit mode renders a <span>, otherwise a <Link>.
 * Prevents accidental navigation when trying to edit nav labels.
 * External URLs (http/https) render as <a> tags.
 */
function NavLink({
  to,
  className,
  isEditMode,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  isEditMode: boolean;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}) {
  if (isEditMode) {
    return <span className={className}>{children}</span>;
  }
  // External links
  if (to.startsWith("http://") || to.startsWith("https://")) {
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const relativePath = useRelativePath();
  const templateType = useTemplateType();
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ctx = useContentEditingSafe();
  const isEditMode = ctx?.isEditMode ?? false;
  const cart = useCartSafe();
  const isCatalogMode = useIsCatalogMode();
  const { visibleNav } = useCustomNav();

  // Select config based on template type
  const isStore = templateType === "store";
  const identity = isStore ? storeIdentity : brandingIdentity;
  const contact = useBusinessInfo();

  // Use merged navigation (default + custom, minus hidden)
  const navigation = visibleNav;

  // Detect backend/admin pages — hide template nav on these
  const isBackendPage = BACKEND_PAGES.includes(relativePath);

  /* ══════ Color Palettes ══════ */

  // Store = dark navy theme, Branding = clean white theme
  const theme = isStore ? {
    topBg: "bg-[#0a1628]",
    topBorder: "",
    topText: "text-white/50",
    navBg: "bg-[#101d32]",
    navBorder: "border-b border-[#1e2d45]",
    logoText: "text-white",
    logoSub: "text-white/30",
    navText: "text-white/70",
    navHover: "hover:text-white",
    navActive: "text-white",
    navActiveBg: "bg-white/10",
    navActiveBar: "bg-sky-400",
    dropBg: "bg-[#162035]",
    dropBorder: "border border-[#243352]",
    dropText: "text-white/70",
    dropHover: "hover:bg-white/5 hover:text-white",
    dropActiveDot: "bg-sky-400",
    mobileBg: "bg-[#101d32]",
    mobileBorder: "border-t border-[#1e2d45]",
    mobileText: "text-white/70",
    mobileHover: "hover:text-white hover:bg-white/5",
    mobileActive: "text-white bg-white/10",
    hamburger: "text-white/50 hover:text-white",
    phoneBg: "#0ea5e9",
    phoneBgHover: "#0284c7",
    cartText: "text-white/60 hover:text-white hover:bg-white/10",
    divider: "bg-white/10",
  } : {
    topBg: "bg-gray-50",
    topBorder: "",
    topText: "text-gray-400",
    navBg: "bg-white",
    navBorder: "border-b border-gray-200",
    logoText: "text-zap-navy",
    logoSub: "text-gray-400",
    navText: "text-gray-600",
    navHover: "hover:text-zap-navy",
    navActive: "text-zap",
    navActiveBg: "bg-zap-pale",
    navActiveBar: "bg-zap",
    dropBg: "bg-white",
    dropBorder: "border border-gray-200",
    dropText: "text-gray-600",
    dropHover: "hover:bg-zap-pale/50 hover:text-zap",
    dropActiveDot: "bg-zap",
    mobileBg: "bg-white",
    mobileBorder: "border-t border-gray-100",
    mobileText: "text-gray-600",
    mobileHover: "hover:text-zap hover:bg-gray-50",
    mobileActive: "text-zap bg-zap-pale",
    hamburger: "text-gray-500 hover:text-zap-navy",
    phoneBg: "#052b7a",
    phoneBgHover: "#0a3c9e",
    cartText: "text-gray-500 hover:text-zap-navy hover:bg-gray-100",
    divider: "bg-gray-200",
  };

  /* ══════ Dropdown handlers with delay ══════ */
  const handleDropdownEnter = useCallback((path: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setOpenDropdown(path);
  }, []);
  const handleDropdownLeave = useCallback(() => {
    dropdownTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  }, []);
  useEffect(() => () => { if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current); }, []);

  const isActive = (path: string) => relativePath === path;
  const isParentActive = (item: typeof navigation[0]) =>
    isActive(item.path) || (item.children?.some((c) => isActive(c.path)) ?? false);

  return (
    <header className={`sticky top-0 z-50 shadow-sm`}>
      {/* ═══════ Top Utility Bar ═══════ */}
      <div className={`${theme.topBg} ${theme.topBorder}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            {isEditMode ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: "linear-gradient(135deg, #052b7a 0%, #1a5bc4 100%)" }}>
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col">
                  <EditableText
                    contentKey="header:brand-name"
                    defaultValue={identity.name}
                    as="span"
                    className={`${theme.logoText} text-lg leading-tight`}
                    style={{ fontWeight: 700 }}
                  />
                  <EditableText
                    contentKey="header:brand-name-heb"
                    defaultValue={identity.nameHebrew}
                    as="span"
                    className={`${theme.logoSub} text-[10px] leading-tight`}
                  />
                </div>
              </div>
            ) : (
              <Link to="/" className="flex items-center gap-3 group">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: "linear-gradient(135deg, #052b7a 0%, #1a5bc4 100%)" }}>
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div className="flex flex-col">
                  <EditableText
                    contentKey="header:brand-name"
                    defaultValue={identity.name}
                    as="span"
                    className={`${theme.logoText} text-lg leading-tight`}
                    style={{ fontWeight: 700 }}
                  />
                  <EditableText
                    contentKey="header:brand-name-heb"
                    defaultValue={identity.nameHebrew}
                    as="span"
                    className={`${theme.logoSub} text-[10px] leading-tight`}
                  />
                </div>
              </Link>
            )}

            {/* Right side: Phone + Cart + Hamburger */}
            {!isBackendPage && (
              <div className="flex items-center gap-2">
                {/* Cart icon — store templates only (hidden in catalog mode) */}
                {isStore && cart && !isCatalogMode && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={cart.toggleCart}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") cart.toggleCart(); }}
                    className={`relative p-2.5 rounded-xl cursor-pointer transition-colors ${theme.cartText}`}
                    title={"\u05E1\u05DC \u05E7\u05E0\u05D9\u05D5\u05EA"}
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {cart.totalItems > 0 && (
                      <span
                        className="absolute -top-0.5 -left-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 bg-red-500 text-white text-[10px] rounded-full leading-none"
                        style={{ fontWeight: 700 }}
                      >
                        {cart.totalItems > 99 ? "99+" : cart.totalItems}
                      </span>
                    )}
                  </span>
                )}

                {/* Phone CTA */}
                {isEditMode ? (
                  <div
                    className="hidden sm:flex items-center gap-2 text-white px-5 py-2 rounded-xl text-sm"
                    style={{ fontWeight: 600, backgroundColor: theme.phoneBg }}
                  >
                    <Phone className="w-4 h-4" />
                    <EditableText
                      contentKey="biz:contact:seo-phone"
                      defaultValue={contact.phone}
                      as="span"
                    />
                  </div>
                ) : (
                  <a
                    href={contact.phoneHref}
                    className="hidden sm:flex items-center gap-2 text-white px-5 py-2 rounded-xl text-sm"
                    style={{ fontWeight: 600, backgroundColor: theme.phoneBg, transition: "background-color 0.2s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.phoneBgHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.phoneBg)}
                  >
                    <Phone className="w-4 h-4" />
                    {contact.phone}
                  </a>
                )}

                {/* Hamburger */}
                <button
                  type="button"
                  onClick={() => setMobileOpen(!mobileOpen)}
                  className={`btnReset lg:hidden p-2 rounded-lg cursor-pointer ${theme.hamburger}`}
                  style={{ backgroundColor: "transparent" }}
                >
                  {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══════ Desktop Navigation Bar ═══════ */}
      {!isBackendPage && (
        <div className={`hidden lg:block ${theme.navBg} ${theme.navBorder}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <nav className="flex items-center gap-0 h-12" style={{ overflow: "visible" }}>
              {navigation.map((item, idx) => {
                const active = isParentActive(item);
                const hasChildren = !!item.children && item.children.length > 0;

                return (
                  <div
                    key={item.path}
                    className="relative flex-shrink-0"
                    onMouseEnter={() => hasChildren && handleDropdownEnter(item.path)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <NavLink
                      to={item.path}
                      isEditMode={isEditMode}
                      className={`
                        relative flex items-center gap-1.5 px-4 h-12 text-[13px] transition-colors cursor-pointer
                        ${active
                          ? `${theme.navActive}`
                          : `${theme.navText} ${theme.navHover}`
                        }
                      `}
                      style={{ fontWeight: active ? 700 : 500 }}
                    >
                      <EditableText
                        contentKey={`nav:${item.path}`}
                        defaultValue={item.label}
                        as="span"
                      />
                      {hasChildren && (
                        <ChevronDown
                          className={`w-3 h-3 transition-transform ${openDropdown === item.path ? "rotate-180" : ""}`}
                          style={{ opacity: 0.5 }}
                        />
                      )}
                      {/* Active indicator bar */}
                      {active && (
                        <span
                          className={`absolute bottom-0 right-4 left-4 h-[2.5px] rounded-full ${theme.navActiveBar}`}
                        />
                      )}
                    </NavLink>

                    {/* Dropdown */}
                    {hasChildren && openDropdown === item.path && (
                      <div
                        className={`absolute top-full right-0 mt-0 pt-1 z-50`}
                        onMouseEnter={() => handleDropdownEnter(item.path)}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <div
                          className={`${theme.dropBg} ${theme.dropBorder} rounded-xl shadow-xl py-2 min-w-[220px]`}
                          style={{ backdropFilter: "blur(12px)" }}
                        >
                          {item.children!.map((child) => {
                            const childActive = isActive(child.path);
                            return (
                              <NavLink
                                key={child.path}
                                to={child.path}
                                isEditMode={isEditMode}
                                className={`
                                  flex items-center gap-3 px-4 py-2.5 text-[13px] transition-colors
                                  ${childActive
                                    ? theme.navActive
                                    : `${theme.dropText} ${theme.dropHover}`
                                  }
                                `}
                                style={{ fontWeight: childActive ? 600 : 400 }}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                    childActive ? theme.dropActiveDot : "bg-current opacity-25"
                                  }`}
                                />
                                <EditableText
                                  contentKey={`nav:${child.path}`}
                                  defaultValue={child.label}
                                  as="span"
                                />
                              </NavLink>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* ═══════ Mobile Nav ═══════ */}
      {!isBackendPage && mobileOpen && (
        <div className={`lg:hidden max-h-[80vh] overflow-y-auto ${theme.mobileBg} ${theme.mobileBorder}`}>
          <nav className="px-4 py-3 space-y-0.5">
            {navigation.map((item) => {
              const hasChildren = !!item.children && item.children.length > 0;
              const active = isParentActive(item);
              const expanded = mobileExpanded === item.path;

              return (
                <div key={item.path}>
                  <div className="flex items-center">
                    <NavLink
                      to={item.path}
                      isEditMode={isEditMode}
                      onClick={() => {
                        if (hasChildren) {
                          setMobileExpanded(expanded ? null : item.path);
                        } else {
                          setMobileOpen(false);
                        }
                      }}
                      className={`
                        flex-1 flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-colors
                        ${active ? theme.mobileActive : `${theme.mobileText} ${theme.mobileHover}`}
                      `}
                      style={{ fontWeight: active ? 600 : 500 }}
                    >
                      <EditableText
                        contentKey={`nav:${item.path}`}
                        defaultValue={item.label}
                        as="span"
                      />
                      {hasChildren && (
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                          style={{ opacity: 0.4 }}
                        />
                      )}
                    </NavLink>
                  </div>
                  {hasChildren && expanded && (
                    <div className="pr-4 pb-1 space-y-0.5">
                      {item.children!.map((child) => {
                        const childActive = isActive(child.path);
                        return (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            isEditMode={isEditMode}
                            onClick={() => setMobileOpen(false)}
                            className={`
                              flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm transition-colors
                              ${childActive ? theme.mobileActive : `${theme.mobileText} ${theme.mobileHover}`}
                            `}
                            style={{ fontWeight: childActive ? 600 : 400, fontSize: "13px" }}
                          >
                            <ChevronLeft className="w-3 h-3" style={{ opacity: 0.3 }} />
                            <EditableText
                              contentKey={`nav:${child.path}`}
                              defaultValue={child.label}
                              as="span"
                            />
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Mobile cart button — store only */}
            {isStore && cart && !isCatalogMode && (
              <span
                role="button"
                tabIndex={0}
                onClick={() => { cart.toggleCart(); setMobileOpen(false); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { cart.toggleCart(); setMobileOpen(false); } }}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm mt-2 cursor-pointer ${
                  isStore
                    ? "bg-white/10 text-white hover:bg-white/20"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                style={{ fontWeight: 600 }}
              >
                <ShoppingCart className="w-4 h-4" />
                {`\u05E1\u05DC \u05E7\u05E0\u05D9\u05D5\u05EA (${cart.totalItems})`}
              </span>
            )}

            {/* Phone CTA in mobile */}
            {isEditMode ? (
              <div
                className="flex items-center justify-center gap-2 text-white px-4 py-3 rounded-xl text-sm mt-3"
                style={{ fontWeight: 600, backgroundColor: theme.phoneBg }}
              >
                <Phone className="w-4 h-4" />
                <EditableText
                  contentKey="biz:contact:seo-phone"
                  defaultValue={contact.phone}
                  as="span"
                />
              </div>
            ) : (
              <a
                href={contact.phoneHref}
                className="flex items-center justify-center gap-2 text-white px-4 py-3 rounded-xl text-sm mt-3"
                style={{ fontWeight: 600, backgroundColor: theme.phoneBg }}
              >
                <Phone className="w-4 h-4" />
                {contact.phone}
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}